import type { ComponentPropsWithRef } from "react";
import { cn, DISABLED, FOCUS_RING } from "./cn";

export const SWITCH_SIZE_CLASSES = {
  xs: {
    track: "h-4 w-8",
    knob: "h-3 w-3",
    knobOn: "translate-x-4",
    knobOff: "translate-x-0.5",
    knobAfter: "after:h-3 after:w-3",
    knobAfterOn: "peer-checked:after:translate-x-4",
  },
  sm: {
    track: "h-5 w-9",
    knob: "h-4 w-4",
    knobOn: "translate-x-4",
    knobOff: "translate-x-0.5",
    knobAfter: "after:h-4 after:w-4",
    knobAfterOn: "peer-checked:after:translate-x-4",
  },
  md: {
    track: "h-6 w-11",
    knob: "h-5 w-5",
    knobOn: "translate-x-5",
    knobOff: "translate-x-0.5",
    knobAfter: "after:h-5 after:w-5",
    knobAfterOn: "peer-checked:after:translate-x-5",
  },
} as const satisfies Record<
  string,
  {
    track: string;
    knob: string;
    knobOn: string;
    knobOff: string;
    knobAfter: string;
    knobAfterOn: string;
  }
>;

export type SwitchSize = keyof typeof SWITCH_SIZE_CLASSES;

const TRACK_BASE =
  "relative inline-flex shrink-0 items-center rounded-full transition-colors";
const TRACK_ON = "bg-primary-500";
const TRACK_OFF = "bg-subtle border border-line";
const KNOB_BASE = "inline-block transform rounded-full transition-transform";
const KNOB_ON = "bg-on-primary";
const KNOB_OFF = "bg-content-muted";

const PEER_TRACK =
  "peer-checked:bg-primary-500 peer-checked:border-transparent peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/40";
const PEER_KNOB =
  "after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:rounded-full after:transition-transform after:bg-content-muted peer-checked:after:bg-on-primary";

export interface SwitchTrackProps {
  checked?: boolean;
  peer?: boolean;
  size?: SwitchSize;
  className?: string;
}

export function SwitchTrack({
  checked = false,
  peer = false,
  size = "md",
  className,
}: SwitchTrackProps) {
  const scale = SWITCH_SIZE_CLASSES[size];

  if (peer)
    return (
      <span
        aria-hidden
        className={cn(
          TRACK_BASE,
          scale.track,
          TRACK_OFF,
          PEER_TRACK,
          PEER_KNOB,
          scale.knobAfter,
          scale.knobAfterOn,
          className,
        )}
      />
    );

  return (
    <span
      aria-hidden
      className={cn(
        TRACK_BASE,
        scale.track,
        checked ? TRACK_ON : TRACK_OFF,
        className,
      )}
    >
      <span
        className={cn(
          KNOB_BASE,
          scale.knob,
          checked ? cn(KNOB_ON, scale.knobOn) : cn(KNOB_OFF, scale.knobOff),
        )}
      />
    </span>
  );
}

export interface SwitchProps
  extends Omit<ComponentPropsWithRef<"button">, "onChange" | "children"> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: SwitchSize;
}

export function Switch({
  checked,
  onChange,
  size = "md",
  disabled,
  className,
  type = "button",
  ...rest
}: SwitchProps) {
  return (
    <button
      {...rest}
      type={type}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "rounded-full",
        FOCUS_RING,
        DISABLED,
        disabled ? undefined : "cursor-pointer",
        className,
      )}
    >
      <SwitchTrack checked={checked} size={size} />
    </button>
  );
}
